import QueryString from "query-string";
import Cookie from "js-cookie";
import { v4 } from "uuid";
import { AnyObject, APIResponse } from "../models";

import { getCSRFCookieName } from "./cookie";
import { getApiServiceURL } from "./domains";
import { autoNormalize, autoFormat } from "../redux/autoNormalize";
import { ErrorCode, LingoError } from "../constants/lingoError";
import getCustomDomainComponents from "./getCustomDomainComponents";
import { settings } from "shared";

type CallApiProps = {
  endpoint: string;
  data?: AnyObject;
  formData?: FormData;
  method?: string;
  query?: AnyObject;
  onUploadProgress?: (percent: number) => void;
};

export const callApi = async <Result>({
  endpoint,
  method = "GET",
  data,
  formData,
  query,
  normalize = true,
  onUploadProgress,
}: CallApiProps & { normalize?: boolean }): Promise<APIResponse<Result>> => {
  const response = await callApiWithRawResponse<{
    success: boolean;
    result: Result;
    error: LingoError;
  }>({
    endpoint,
    method,
    data,
    formData,
    query,
    onUploadProgress,
  });

  if (response.success) {
    return normalize ? autoNormalize(response.result) : autoFormat(response.result);
  }

  let error = response.error;

  // Some www endpoints just return strings still
  if (typeof error === "string") {
    error = {
      message: error,
      code: 0,
    };
  }
  throw {
    error: true,
    ...error,
  };
};

export const callApiWithRawResponse = <Result>({
  endpoint,
  method = "GET",
  data,
  formData,
  query,
  onUploadProgress,
}: CallApiProps): Promise<Result> => {
  if (data && formData) {
    throw new Error("Cannot call API with both data and formData.");
  }

  return new Promise((resolve, reject) => {
    let fullUrl = getApiServiceURL(endpoint);
    if (query) {
      fullUrl += `?${QueryString.stringify(query)}`;
    }
    const xhr = new XMLHttpRequest();

    xhr.open(method, fullUrl, true);
    xhr.withCredentials = true;
    setRequestHeaders(xhr, data, method);

    // Note: if we ever refactor this to use fetch,
    // we will lose the ability to track upload progress
    // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/Using_XMLHttpRequest#monitoring_progress
    if (onUploadProgress && formData) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onUploadProgress?.(percentComplete);
        }
      };
    }
    xhr.onload = function () {
      try {
        if (!this.responseText) {
          return reject({
            error: true,
            code: this.status,
            message: "An error occured",
          });
        }

        const response = JSON.parse(this.responseText) as AnyObject;
        if (this.status === 400) {
          reportError(fullUrl, data, this.status, response.error as LingoError | string);
        }
        return resolve(response as Result);
      } catch (e) {
        // If any response handling code fails, report error, then resolve as a failure
        if (settings.environment !== "production") {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          console.error(e, e.stack);
        }
        return reject({
          code: ErrorCode.unknown,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          message: e.message ?? "An unknown error occurred",
        });
      }
    };
    xhr.onerror = e => {
      console.error(e);
      reject({
        code: ErrorCode.unknown,
        message: "Unable to complete request",
      });
    };

    xhr.send(getRequestBody(data, formData));
  });
};

export const getRequestBody = function (data: AnyObject, formData: FormData) {
  if (formData) return formData;
  if (data) return JSON.stringify(data);
  return null;
};

export const setRequestHeaders = function (xhr: XMLHttpRequest, data: AnyObject, method: string) {
  const accessToken = localStorage.getItem("token");
  if (accessToken) {
    xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
  }

  xhr.setRequestHeader("X-lingo-fingerprint", getFingerprint());

  const csrfCookieName = getCSRFCookieName(),
    csrfCookieValue = Cookie.get(csrfCookieName);
  if (csrfCookieValue && !csrfSafeMethod(method)) {
    xhr.setRequestHeader("X-CSRFToken", csrfCookieValue);
  }

  if (data) {
    xhr.setRequestHeader("Content-Type", "application/json");
  }
  return xhr;
};

const getFingerprint = function () {
  let fingerprint = Cookie.get("user_fingerprint");
  if (!fingerprint) {
    fingerprint = v4();
    const domain = getCustomDomainComponents().host ?? "lingoapp.com";
    Cookie.set("user_fingerprint", fingerprint, { expires: 365, domain });
  }
  return fingerprint;
};

const csrfSafeMethod = function (method: string) {
  return /^(GET|HEAD|OPTIONS|TRACE)$/.test(method);
};

export const reportError = function (
  url: string,
  data: AnyObject,
  statusCode: number,
  errorObj: LingoError | string
) {
  const message = getResponseErrorMessage(errorObj);
  if (message && settings.environment !== "production") {
    console.error(`Error from ${url}: ${message}`);
  }
};

const getResponseErrorMessage = function (error: LingoError | string) {
  // Take an error object, and return a string to display in error tracking service
  if (typeof error === "string") return error;
  if (error.message) return error.message;
};
